import styled from 'styled-components';
import { zIndex } from 'tokens';

interface DsControlProps {
	isEmpty: boolean;
	isActive: boolean;
	isLoading: boolean;
	compactMode?: 'full' | 'compact';
}

export const DsControl = styled.button<DsControlProps>`
	display: flex;
	align-items: center;
	gap: 6px;
	height: 30px;
	padding: ${({ compactMode }) => (compactMode === 'compact' ? '0 8px' : '0 10px')};
	border-radius: 4px;
	cursor: default;
	transition: all 0.2s ease;
	position: relative;
	user-select: none;
	font-size: 12px;
	font-weight: 500;
	font-family: inherit;
	border: none;
	background: transparent;

	/* Empty state */
	${({ isEmpty, theme }) =>
		isEmpty &&
		`
		border: 1px solid ${theme.gray[200]};
		color: ${theme.gray[600]};

		&:hover {
			border-style: solid;
			background: ${theme.gray[200]};
			color: ${theme.gray[1000]};
		}
	`}

	/* Active state */
	${({ isActive, theme }) =>
		isActive &&
		`
		border: 1px solid ${theme.gray[200]};
		padding-left: 9px;
		color: ${theme.gray[1000]};

		&:hover {
			background: ${theme.gray[200]};
			box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
		}
	`}

	/* Loading state */
	${({ isLoading, theme }) =>
		isLoading &&
		`
		border: 1px solid ${theme.gray[200]};
		color: ${theme.gray[1000]};
	`}
`;

export const DsIcon = styled.div`
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`;

export const DsDot = styled.div<{ color?: 'blue' | 'red' | 'green' }>`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	flex-shrink: 0;
	background: ${({ color, theme }) => {
		if (color === 'red') return theme.red[500];
		if (color === 'green') return theme.green[600];
		return theme.blue[500];
	}};
	box-shadow: ${({ color, theme }) => {
		if (color === 'red') return `0 0 4px ${theme.red[500]}66`;
		if (color === 'green') return `0 0 4px ${theme.green[600]}66`;
		return `0 0 4px ${theme.blue[500]}66`;
	}};
`;

export const DsText = styled.span`
	font-size: 11px;
	font-weight: 500;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 120px;
`;

export const DsSubtext = styled.span<{ success?: boolean }>`
	font-size: 9px;
	color: ${({ success, theme }) => (success ? theme.green[600] : theme.gray[600])};
	margin-left: 2px;
`;

export const DsBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 16px;
	height: 16px;
	padding: 0 5px;
	background: ${({ theme }) => theme.blue[500]};
	color: ${({ theme }) => theme.white};
	font-size: 10px;
	font-weight: 500;
	border-radius: 8px;
	flex-shrink: 0;
`;

export const DsChevron = styled.div`
	width: 10px;
	height: 10px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ theme }) => theme.gray[600]};
`;

export const DropdownContainer = styled.div`
	position: relative;
	display: inline-block;
	text-align: left;
`;

export const Dropdown = styled.div<{ isOpen: boolean; position: 'above' | 'below' | 'center' }>`
	position: absolute;
	${({ position }) => {
		if (position === 'above') {
			return `
		bottom: calc(100% + 8px);
		transform-origin: bottom center;
	`;
		} else if (position === 'below') {
			return `
		top: calc(100% + 8px);
		transform-origin: top center;
	`;
		} else {
			// center position
			return `
		top: 50%;
		transform-origin: center center;
		max-height: calc(100vh - 32px);
	`;
		}
	}}
	left: 0;
	width: 260px;
	background: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 8px;
	padding: 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
	z-index: ${zIndex.island};
	opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
	pointer-events: ${({ isOpen }) => (isOpen ? 'all' : 'none')};
	transform: ${({ isOpen, position }) => {
		if (!isOpen) {
			if (position === 'above') {
				return 'translateY(-4px) scale(0.95)';
			} else if (position === 'below') {
				return 'translateY(4px) scale(0.95)';
			} else {
				// Center position: only scale, no translateY offset to avoid sliding
				return 'translateY(-50%) scale(0.95)';
			}
		} else {
			if (position === 'center') {
				return 'translateY(-50%) scale(1)';
			}
			return 'translateY(0) scale(1)';
		}
	}};
	transition: ${({ position }) =>
		position === 'center'
			? 'opacity 0.25s ease-out'
			: 'opacity 0.25s ease-out, transform 0.25s ease-out'};
	text-align: left;
`;

export const DsStatusIcon = styled.div`
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`;

export const DsStatusContainer = styled.div`
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`;
